import React, { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import objectSupport from 'dayjs/plugin/objectSupport'
import { useIntl } from 'react-intl'

import {
  Select,
  DatePicker,
  Form,
  Button,
  Flex,
  Row,
  Col,
  Typography,
  Collapse,
  Tooltip,
  Input,
} from 'antd'
import { useShifts } from '../hooks/Shifts'
import { useUserPlant, useUserTestZones } from '../hooks/User'

function ShiftLabel(intl, shift) {
  let shiftTokens = shift.split(' ')
  if (shiftTokens.length === 2) {
    if (!isNaN(shiftTokens[1])) {
      return (
        intl.formatMessage({ id: 'faultReport.filters.shifts.label' }) +
        ' ' +
        shiftTokens[1]
      )
    } else {
      return intl.formatMessage({
        id: 'faultReport.filters.shifts.supersetLabel',
      })
    }
  }
  return shift
}

export const getAllShiftsItem = () => {
  const todayStart = dayjs().startOf('day')
  const todayEnd = dayjs().endOf('day')
  return {
    name: 'All Day',
    periods: {
      start: todayStart.utc().format('HH:mm'),
      end: todayEnd.utc().format('HH:mm'),
    },
  }
}

export const getShiftOptions = (plantShifts) => {
  // Shifts hours are in UTC
  const allShiftsItem = getAllShiftsItem()
  const shiftOptions = {
    ...{ [allShiftsItem.name]: allShiftsItem.periods },
    ...plantShifts.reduce((acc, shift) => {
      return {
        ...acc,
        [shift.name]: { start: shift.starts_at, end: shift.ends_at },
      }
    }, {}),
  }
  return shiftOptions
}

const ShiftSelect = ({ disabled, ...rest }) => {
  const intl = useIntl()
  const [userPlant] = useUserPlant()
  const { isLoading, shifts: plantShifts } = useShifts({
    plant_name: userPlant,
  })

  const shiftOptions = getShiftOptions(plantShifts || [])

  return (
    <Select
      style={{ minWidth: 110 }}
      name="shift"
      disabled={disabled || isLoading}
      data-cy="report-shift"
      loading={isLoading}
      {...rest}
    >
      {Object.keys(shiftOptions).map((shift) => (
        <Select.Option key={shift} value={shift}>
          {ShiftLabel(intl, shift)}
        </Select.Option>
      ))}
    </Select>
  )
}

const setTimeToDate = (date, time) => {
  const year = date.year()
  const month = date.month()
  const day = date.date()

  const hours = time.hour()
  const minutes = time.minute()
  const seconds = time.second()

  const combinedUTC = dayjs.utc({
    year,
    month,
    day,
    hour: hours,
    minute: minutes,
    second: seconds,
  })

  return combinedUTC
}

export const getShiftDateTime = ({ period, currentShiftName, shifts }) => {
  const allShiftsItem = getAllShiftsItem()
  const startDate = period[0].startOf('day')
  const endDate = period[1].endOf('day')
  const adjustedShifts = {}
  let daySwitched = false
  let allDayStart = null
  let allDayEnd = null
  for (const [shiftName, hours] of Object.entries(shifts)) {
    if (shiftName === allShiftsItem.name) {
      continue
    }
    const startHour = dayjs.utc(hours.start, 'HH:mm')
    const endHour = dayjs.utc(hours.end, 'HH:mm')
    let startAt = setTimeToDate(startDate, startHour)
    let endAt = setTimeToDate(endDate, endHour)

    // Shifts are list of start and end time that cover 24 hours.
    // For example, one shift can be in period 03:00-06:00.
    // BUT it is possible that shift starts at one day but ends at the next day.
    // In this case, its period will be something like this: 18:00-03:00.
    if (endAt.hour() < startAt.hour()) {
      // Cover case when shift starts at one day but ends at the next day.
      daySwitched = true
      endAt = endAt.add(1, 'days')
    } else if (daySwitched) {
      // Cover case if previous shift has date change.
      endAt = endAt.add(1, 'days')
      startAt = startAt.add(1, 'days')
    }

    // Build "All Shifts" entry from beginning of first shift and end of last shift
    if (allDayStart == null) allDayStart = startAt
    allDayEnd = endAt

    adjustedShifts[shiftName] = {
      startAt,
      endAt,
    }
  }

  adjustedShifts[allShiftsItem.name] = {
    startAt: allDayStart,
    endAt: allDayEnd,
  }
  return adjustedShifts[currentShiftName]
}

export default ShiftSelect
